import React from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useQueryString } from "hooks/general-hooks";
import { validParams } from "query_parameters/valid_parameters";
import { ReactComponent as FilterIcon } from "svg/filters-icon.svg";
import * as generalHelper from "lib/general-helper.js";
import MediaQuery from "components/MediaQuery";
import Class from "./Filters/Class";
import Spec from "./Filters/Spec";
import Dungeon from "./Filters/Dungeon";
import Encounter from "./Filters/Encounter";
import Affix from "./Filters/Affix";
import { validateFilter, validatePathname } from "query_parameters/paramsHelper";
import { useWowPath } from "../../hooks/path-hooks";
import EncounterType from "./Filters/EncounterType";
import DifficultyFilter from "./Filters/DifficultyFilter";
import PvPGameModes from "./Filters/PvPGameModes";

const FilterManager = (props) => {
  const location = useLocation();
  const queryParams = useQueryString();
  const history = useHistory();

  const { encounterType: buildType, classType: selectedClass, specType: selectedSpec } = useWowPath();
  const { disable, page } = props;

  const updateQueryParams = (newFilter, pathName) => {
    // delete filters that we don't need
    let removeKeys = disable || [];
    let curFilter = generalHelper.removeKeys(newFilter, removeKeys);

    const newLocation = {
      ...location,
      pathname: pathName ? pathName : location.pathname,
    };

    newLocation.pathname = validatePathname(newLocation.pathname, page);
    curFilter = validateFilter(curFilter, page, newLocation.pathname);

    if (pathName !== location.pathname) {
      generalHelper.updateQueryParams(history, newLocation.pathname, curFilter);
    } else {
      generalHelper.updateQueryParamsIfChanged(history, newLocation, curFilter);
    }
  };

  const onFilterChange = (filterType, filterValue, alterQueryParams = true, alterUrlPos = -1) => {
    let newFilter = { [filterType]: filterValue };
    newFilter = { ...queryParams, ...newFilter };
    if (filterType === "encounterType" && filterValue === "raid") {
      delete queryParams["affix"];
    }
    let newUrl = location.pathname;
    if (alterUrlPos != -1) {
      let splitUrl = newUrl.split("/");
      splitUrl[alterUrlPos] = filterValue;

      // Have to add a leading '/' for path info stuff
      newUrl = "/" + splitUrl.filter((x) => x !== "").join("/");
    }

    const filterToUse = alterQueryParams ? newFilter : queryParams;
    const urlToUse = alterUrlPos != -1 ? newUrl : location.pathname;

    updateQueryParams(filterToUse, urlToUse);
  };

  let validPageParams = validParams[page] || {};
  let defaultParams = validPageParams.default ? { ...validPageParams.default } : {};

  const filters = {
    ...defaultParams,
    ...queryParams,
  };

  const filterProps = {
    ...props,
    filters,
    onFilterChange,
  };

  const options = { ...validPageParams };

  // Returns classname to check if default option is selected
  const isDefault = (filterKey, currentFilters, defaultFilters) => {
    return (currentFilters && currentFilters[filterKey]) === (defaultFilters && defaultFilters[filterKey])
      ? ""
      : "filter_not-default";
  };

  const selectedDungeon = filters["dungeon"] ? filters["dungeon"] : buildType === "raid" ? "amirdrassil" : "all_dungeons";
  const selectedEncounter = filters["encounter"] ? filters["encounter"] : buildType == "mythic" ? "all_dungeons" : "all_bosses";
  const encounterOptions = buildType == "mythic" ? options.mythics : options.encounters?.[selectedDungeon];

  return (
    <div className={`filter-manager`}>
      <div className="filter-width-wrapper">
        <div className="filter-manager_container">
          <div className="filter-manager_label">
            <FilterIcon />
            <MediaQuery min="TABLET" max="DESKTOP_LARGE">
              <span>Filters</span>
            </MediaQuery>
          </div>
          <Class
            {...filterProps}
            options={options.class}
            selectedVal={selectedClass}
            isDefault={isDefault("class", filters, {})}
          />
          <Spec {...filterProps} options={options.specs[selectedClass]} selectedSpec={selectedSpec} />
          <EncounterType {...filterProps} options={options.encounterTypes} selectedVal={buildType} disabled={page === "pvp"} />
          {(options?.raids?.length ?? 0) > 2 && (
            <Dungeon
              {...filterProps}
              options={options.raids}
              selectedVal={selectedDungeon}
              disabled={buildType == "mythic"}
              isDefault={isDefault("dungeon", filters, {})}
            />
          )}
          {buildType === "raid" && page !== "pvp" && (
            <DifficultyFilter {...filterProps} options={options.difficulty} value={filters.difficulty} />
          )}
          <Encounter
            {...filterProps}
            options={encounterOptions}
            selectedVal={selectedEncounter}
            isDefault={isDefault("encounter", filters, {})}
            disabled={page === "pvp"}
          />
          <Affix
            {...filterProps}
            disabled={buildType === "raid" || page === "pvp"}
            options={options.affixes}
            selectedVal={filters["affix"] ?? "all"}
          />
          <PvPGameModes
            {...filterProps}
            disabled={page !== "pvp"}
            options={options.gameModes}
            selectedVal={filters["gameMode"] ?? "3v3"}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterManager;

FilterManager.defaultProps = {
  enable: [], // Array of keys to enable. playerType is disabled by default
  disable: [], // Array of keys to disable/hide specific filters
};
